import { useState } from "react";
import validator from "validator";
import { useDebounce } from "use-debounce";
import { keepPreviousData } from "@tanstack/react-query";
import { MultibankingApiProvider } from "@api";
import { useGetMyAuthorization } from "@api/v1/hooks/useAuthorization";
import { useBanksSearch } from "@api/v1/hooks/useBanksSearch";
import { UserAuthorization } from "@bn/api/apiv1";
import { useRoutes } from "../../../hooks/useRoutes";
import { groupBankByName } from "../utils";

const hasFinApiAccount = (authorization: UserAuthorization) =>
  authorization.thirdPartyAccounts?.includes("FINAPI");

interface Options {
  defaultSearchValue?: string;
  onSearchChange?: (value: string) => void;
}

/**
 * Encapsulates the data fetching and bank-selection navigation for the
 * bank-select modal.
 */
export const useImportBankSelect = ({
  defaultSearchValue = "",
  onSearchChange,
}: Options = {}) => {
  const { pushLegalNote, pushImportConnection } = useRoutes();
  const { data: authorization } = useGetMyAuthorization();

  const [searchValue, setSearchValue] = useState(defaultSearchValue);

  const handleChange = (value: string) => {
    setSearchValue(value);
    onSearchChange?.(value);
  };

  const [value] = useDebounce(searchValue, 300);

  const valueWithoutSpaces =
    value && validator.isIBAN(value) ? value.replace(/\s/g, "") : value;

  const { data, isFetching } = useBanksSearch(
    { search: valueWithoutSpaces },
    { placeholderData: keepPreviousData }
  );

  const banks = groupBankByName(data?.banks);

  const onSelect = (
    id: number | undefined,
    multibankingProvider: MultibankingApiProvider,
    wealthApiSlug?: string
  ) => {
    if (!authorization) return;
    const bankSlug =
      multibankingProvider === "WEALTH_API" ? wealthApiSlug! : String(id);
    if (hasFinApiAccount(authorization)) {
      void pushImportConnection(bankSlug, multibankingProvider);
    } else {
      pushLegalNote(bankSlug, multibankingProvider);
    }
  };

  return {
    banks,
    isFetching,
    searchValue,
    onChange: handleChange,
    onSelect,
  };
};
